import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../../styling/stylingHelper';
import {
  generateReportFile,
  type GenerateReportFileResult,
  sendReportEmail,
  type ReportDataWarning,
  getReportRecipients,
  type ReportRecipients,
  getInventoryGroups,
  type AdminInventoryGroup,
  type AdminReportType,
  type AdminReportLanguage,
  type LeakageUnitPreview,
  AdminRestaurant,
} from '../../../../services/admin/adminService';
import { dataAlertTypeLabel } from '../../../../services/admin/dataAlertsAdminService';
import {
  getGroupReportOwners,
  sendGroupReport,
  GROUP_REPORT_TYPES,
  type GroupReportOwner,
  type GroupReportType,
} from '../../../../services/admin/reportsAdminService';
import { startOfPeriod, addPeriod } from '../../../../utils/dateUtils';
import { currencyFormatter } from '../../../../services/helperService';
import { ReportRecipientPicker, type RecipientStaffWithVenues } from './ReportRecipientPicker';

type TFn = ReturnType<typeof useTranslation>['t'];

/**
 * Format a Date as YYYY-MM-DD using its LOCAL date parts (not toISOString, which converts to UTC
 * and rolls midnight-local dates back a day in positive-offset timezones like Amsterdam).
 */
function toLocalISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Hard cap the server enforces per real send (`MAX_REPORT_RECIPIENTS`, superAdmin/reportGenerationRoutes.ts). */
const MAX_REPORT_RECIPIENTS = 25;

/**
 * The window a scheduled run would cover: the last completed ISO week, or — for the monthly
 * report — the previous calendar month. Both ends are inclusive, matching the date inputs.
 */
function previousPeriodWindow(view: 'week' | 'month'): { start: string; end: string } {
  const start = addPeriod(startOfPeriod(new Date(), view), view, -1);
  const end = addPeriod(start, view, 1);
  end.setDate(end.getDate() - 1);
  return { start: toLocalISODate(start), end: toLocalISODate(end) };
}

/** Group mode's own type list — the types a group overview exists for. */
const GROUP_REPORT_TYPE_LABEL_KEYS: Record<GroupReportType, string> = {
  weekly: 'adminTesting.generateReport.reportTypeWeekly',
  revenue: 'adminTesting.generateReport.reportTypeRevenue',
  monthly: 'adminTesting.generateReport.reportTypeMonthly',
  priceAlerts: 'adminTesting.generateReport.reportTypePriceAlerts',
};

/**
 * What went out with the report, not what stopped it: since phase 8 open data alerts never hold
 * a send — the report is mailed with a warning section in its body, and the same lines are
 * reported back here so the super admin sees what the client will read.
 */
function DataWarningsBanner({ warnings, t }: { warnings: ReportDataWarning[]; t: TFn }) {
  return (
    <div
      className={cn(
        'mb-4 p-3 rounded-lg border',
        'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
      )}
    >
      <p className={cn('text-amber-800 dark:text-amber-200', smallTextSize, semiBoldText)}>
        {t('adminTesting.generateReport.dataWarningsTitle', { count: warnings.length })}
      </p>
      <ul className="mt-1 space-y-0.5">
        {warnings.map((warning, index) => (
          <li key={`${warning.alertType}-${index}`} className="text-xs text-amber-800 dark:text-amber-200 break-words">
            {dataAlertTypeLabel(warning.alertType, (key) => t(key))} · {warning.message}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
        {t('adminTesting.generateReport.dataWarningsHint')}
      </p>
    </div>
  );
}

/** Emails typed into the group-send box: comma, semicolon, whitespace or newline separated. */
function parseRecipientList(raw: string): string[] {
  const seen = new Set<string>();
  return raw
    .split(/[\s,;]+/)
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => {
      if (!entry || seen.has(entry)) return false;
      seen.add(entry);
      return true;
    });
}

/** What the test-email messages need, captured from the files that actually downloaded. */
interface TestEmailContext {
  groupId?: string;
  fileCount: number;
  firstFilename: string;
  restaurantName: string;
}

/**
 * Generate report files for a restaurant: downloads the PDF(s), optionally emails a test copy,
 * and — in real-send mode — mails the report to picked customer recipients.
 */
export function GenerateReportCard({ restaurants }: { restaurants: AdminRestaurant[] }) {
  // Super-admin tooling is always English regardless of the app's selected language.
  const { i18n } = useTranslation();
  // getFixedT returns a new function identity every call — memoize it, or effects
  // that depend on `t` re-run on every render (infinite fetch loop).
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);

  // Generate report file state (defaults to the previous ISO week, Monday–Sunday)
  const [reportFileRestaurantId, setReportFileRestaurantId] = useState('');
  const [reportFileSearchQuery, setReportFileSearchQuery] = useState('');
  const [reportFileType, setReportFileType] = useState<AdminReportType>('revenue');
  // Language the PDF, its filename, and the summary email are rendered in.
  // 'nl' default since 2026-08-06: Dutch is the default report language everywhere
  // (matches SCHEDULE_DEFAULTS server-side); English is the explicit opt-in.
  const [reportFileLanguage, setReportFileLanguage] = useState<AdminReportLanguage>('nl');
  const [reportFileStartDate, setReportFileStartDate] = useState(() => previousPeriodWindow('week').start);
  const [reportFileEndDate, setReportFileEndDate] = useState(() => previousPeriodWindow('week').end);
  const [reportFileLoading, setReportFileLoading] = useState(false);
  const [reportFileMessage, setReportFileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  /** Data-alert warnings the last send carried into the email body. Informational — never blocks. */
  const [dataWarnings, setDataWarnings] = useState<ReportDataWarning[]>([]);
  const [reportFileEmail, setReportFileEmail] = useState('');
  // Real-send mode: the report goes to live customers instead of a single test address.
  const [reportFileSendReal, setReportFileSendReal] = useState(false);
  const [reportFileRecipients, setReportFileRecipients] = useState<ReportRecipients | null>(null);
  const [reportFileRecipientsLoading, setReportFileRecipientsLoading] = useState(false);
  // Emails of the people ticked to receive a real send (admin pre-ticked, staff opt-in).
  const [reportFileSelectedEmails, setReportFileSelectedEmails] = useState<string[]>([]);
  // Per-group inventory report types render one PDF for one inventory group, picked here.
  const [reportFileGroups, setReportFileGroups] = useState<AdminInventoryGroup[] | null>(null);
  const [reportFileGroupsLoading, setReportFileGroupsLoading] = useState(false);
  const [reportFileGroupId, setReportFileGroupId] = useState('');
  const reportFileGroupRequired = reportFileType === 'inventoryOrder' || reportFileType === 'inventoryManagement';
  // The waste/leakage report takes a group OPTIONALLY: no pick = every group combined.
  const reportFileGroupOptional = reportFileType === 'inventoryLeakage';
  const reportFileGroupUsed = reportFileGroupRequired || reportFileGroupOptional;
  // Inventory reports can close on a saved count and compare against an earlier one.
  const [reportFileBaselineSessionId, setReportFileBaselineSessionId] = useState('');
  const [reportFileCountSessionId, setReportFileCountSessionId] = useState('');
  /**
   * Which quantity unit to PREVIEW the leakage report in. '' = the restaurant's saved setting,
   * which is what its real sends use; picking a unit overrides it for this generate/test send
   * only and writes nothing — that is the whole point (check both outputs for a live client
   * without touching their setting).
   */
  const [reportFileLeakageUnit, setReportFileLeakageUnit] = useState<'' | LeakageUnitPreview>('');

  // Group mode: ONE combined email per recipient for a whole admin account — the group overview
  // PDF plus every enabled venue's own PDF. The window and language controls are shared with
  // single mode; only the owner, type, recipients and test flag are its own.
  const [mode, setMode] = useState<'single' | 'group'>('single');
  const [groupOwners, setGroupOwners] = useState<GroupReportOwner[] | null>(null);
  const [groupOwnersLoading, setGroupOwnersLoading] = useState(false);
  // Without this a failed load renders as "no account has two restaurants" — the opposite of
  // what a failed load means.
  const [groupOwnersError, setGroupOwnersError] = useState(false);
  const [groupOwnerId, setGroupOwnerId] = useState('');
  const [groupReportType, setGroupReportType] = useState<GroupReportType>('weekly');
  const [groupRecipientsInput, setGroupRecipientsInput] = useState('');
  const [groupRecipientOptions, setGroupRecipientOptions] = useState<(Omit<ReportRecipients, 'staff'> & { staff: RecipientStaffWithVenues[] }) | null>(null);
  const [groupSelectedEmails, setGroupSelectedEmails] = useState<string[]>([]);
  const [groupRecipientsLoading, setGroupRecipientsLoading] = useState(false);
  const [groupRecipientsError, setGroupRecipientsError] = useState(false);
  const [groupIsTest, setGroupIsTest] = useState(true);
  /**
   * Which of the owner's venues go into this send. Every venue starts ticked (the default group
   * send is the whole account); unticking is how the combined file is simulated without one.
   * A subset only ever REMOVES venues — a ticked venue with the type switched off on
   * Communication → Settings still stays out, exactly as a scheduled run would leave it out.
   */
  const [groupVenueIds, setGroupVenueIds] = useState<string[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupMessage, setGroupMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  /** Filenames the last group send attached — the proof that every venue's PDF came along. */
  const [groupSentFilenames, setGroupSentFilenames] = useState<string[]>([]);

  // In real-send mode, load the selected restaurant's admin + staff and pre-tick the admin.
  // Staff stay unticked: every staff recipient is a deliberate choice per send.
  useEffect(() => {
    if (!reportFileSendReal || !reportFileRestaurantId) {
      setReportFileRecipients(null);
      setReportFileSelectedEmails([]);
      return;
    }
    let cancelled = false;
    const loadRecipients = async () => {
      try {
        setReportFileRecipientsLoading(true);
        const data = await getReportRecipients(reportFileRestaurantId);
        if (cancelled) return;
        setReportFileRecipients(data);
        setReportFileSelectedEmails(data.owner ? [data.owner.email] : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load report recipients:', err);
        setReportFileRecipients(null);
        setReportFileSelectedEmails([]);
        setReportFileMessage({ type: 'error', text: t('adminTesting.generateReport.recipientsError') });
      } finally {
        if (!cancelled) setReportFileRecipientsLoading(false);
      }
    };
    loadRecipients();
    return () => { cancelled = true; };
  }, [reportFileSendReal, reportFileRestaurantId, t]);

  // Load the restaurant's inventory groups once a group-aware report type is selected. No group is
  // pre-picked: which group the report covers is a deliberate choice per generate/send.
  useEffect(() => {
    if (!reportFileGroupUsed || !reportFileRestaurantId) {
      setReportFileGroups(null);
      return;
    }
    let cancelled = false;
    const loadGroups = async () => {
      try {
        setReportFileGroupsLoading(true);
        const data = await getInventoryGroups(reportFileRestaurantId);
        if (cancelled) return;
        setReportFileGroups(data.groups);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load inventory groups:', err);
        setReportFileGroups(null);
        setReportFileMessage({ type: 'error', text: t('adminTesting.generateReport.groupsError') });
      } finally {
        if (!cancelled) setReportFileGroupsLoading(false);
      }
    };
    loadGroups();
    return () => { cancelled = true; };
  }, [reportFileGroupUsed, reportFileRestaurantId, t]);

  // Owners with 2+ restaurants — the only accounts a group report means anything for. Loaded
  // once the mode is picked, not on mount: single mode is the common case and never needs them.
  useEffect(() => {
    if (mode !== 'group' || groupOwners !== null || groupOwnersError) return;
    let cancelled = false;
    const loadOwners = async () => {
      try {
        setGroupOwnersLoading(true);
        const owners = await getGroupReportOwners();
        if (!cancelled) setGroupOwners(owners);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load group report owners:', err);
        setGroupOwnersError(true);
      } finally {
        if (!cancelled) setGroupOwnersLoading(false);
      }
    };
    loadOwners();
    return () => { cancelled = true; };
  }, [mode, groupOwners, groupOwnersError]);

  const reportFileFilteredRestaurants = restaurants.filter(r =>
    r.name.toLowerCase().includes(reportFileSearchQuery.toLowerCase()) ||
    r.ownerEmail.toLowerCase().includes(reportFileSearchQuery.toLowerCase())
  );

  const isReportFileEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reportFileEmail);
  // The inventory report types window themselves off the restaurant's / group's inventory counts.
  const reportFileDatesRequired = reportFileType !== 'inventoryLeakage' && !reportFileGroupRequired;
  const reportFileSelectedGroup = reportFileGroups?.find(g => g.id === reportFileGroupId) ?? null;
  // The order report lists what to re-order against real minimum stock levels — without any
  // configured for the group there is nothing to compare against, so the server would 400.
  const reportFileGroupMissingMinimums =
    reportFileType === 'inventoryOrder' && !!reportFileSelectedGroup && reportFileSelectedGroup.minimumsCount === 0;
  const reportFileGroupBlocked = reportFileGroupRequired && (!reportFileGroupId || reportFileGroupMissingMinimums);
  // Sent only when a group is actually picked — an optional-group type with none picked
  // must send no groupId at all, which is what the server reads as "all groups combined".
  const reportFileGroupIdToSend = reportFileGroupUsed && reportFileGroupId ? reportFileGroupId : undefined;
  // Only the leakage report has a unit setting; the server rejects the field for anything else,
  // so it is never sent from another type's form even if a pick is still in state.
  const reportFileLeakageUnitToSend =
    reportFileType === 'inventoryLeakage' && reportFileLeakageUnit ? reportFileLeakageUnit : undefined;
  const reportFileCountSupported = (reportFileType === 'inventoryLeakage' || reportFileType === 'inventoryManagement') && !!reportFileGroupIdToSend;
  const reportFileEndingCount = reportFileSelectedGroup?.counts.find(c => c.sessionId === reportFileCountSessionId)
    ?? reportFileSelectedGroup?.counts[0] ?? null;
  const reportFileBaselineOptions = reportFileSelectedGroup?.counts.filter(c =>
    reportFileEndingCount && new Date(c.completedAt) < new Date(reportFileEndingCount.completedAt)
  ) ?? [];
  const reportFileBaselineToSend = reportFileCountSupported
    ? reportFileBaselineSessionId || reportFileBaselineOptions[0]?.sessionId
    : undefined;
  const reportFileStartingCount = reportFileBaselineOptions.find(c => c.sessionId === reportFileBaselineToSend);
  // The report builders use the saved counts' UTC calendar dates. Carry those dates in
  // the request too, so send-history metadata does not keep the unrelated week inputs.
  const reportFileStartDateToSend = reportFileCountSupported && reportFileEndingCount
    ? (reportFileStartingCount ?? reportFileEndingCount).completedAt.slice(0, 10) : reportFileStartDate;
  const reportFileEndDateToSend = reportFileCountSupported && reportFileEndingCount
    ? reportFileEndingCount.completedAt.slice(0, 10) : reportFileEndDate;
  const reportFileCountOptions = reportFileCountSupported && reportFileEndingCount
    ? reportFileType === 'inventoryLeakage'
      ? { endingCountId: reportFileEndingCount.countIds[0] }
      : { countSessionId: reportFileEndingCount.sessionId }
    : {};
  const reportFileCountsBlocked = reportFileCountSupported && (
    reportFileGroupsLoading || !reportFileEndingCount ||
    (reportFileType === 'inventoryLeakage' && !reportFileBaselineToSend)
  );

  /**
   * The "[TEST] " copy that optionally follows a file generation. Everything its success message
   * needs is passed in rather than re-read from state, so it describes the files that actually
   * downloaded — it never regenerates them.
   */
  const runTestEmail = async (ctx: TestEmailContext) => {
    try {
      setReportFileLoading(true);
      const result = await sendReportEmail(
        reportFileRestaurantId,
        reportFileType,
        reportFileStartDateToSend,
        reportFileEndDateToSend,
        [reportFileEmail],
        reportFileLanguage,
        true,
        ctx.groupId,
        reportFileBaselineToSend,
        reportFileLeakageUnitToSend,
        reportFileCountOptions
      );
      setDataWarnings(result.dataWarnings ?? []);
      if (result.skipped?.length && result.sent.length === 0) {
        setReportFileMessage({ type: 'success', text: t('adminTesting.reportAllRecipientsSkipped') });
        return;
      }
      setReportFileMessage({
        type: 'success',
        text: ctx.fileCount > 1
          ? t('adminTesting.generateReport.successWithEmailMultiple', { restaurantName: ctx.restaurantName, count: ctx.fileCount, email: reportFileEmail })
          : t('adminTesting.generateReport.successWithEmail', { restaurantName: ctx.restaurantName, filename: ctx.firstFilename, email: reportFileEmail }),
      });
    } catch (emailErr: any) {
      setReportFileMessage({
        type: 'error',
        text: t('adminTesting.generateReport.emailFailedAfterDownload', { error: emailErr.message || t('adminTesting.generateReport.emailError') }),
      });
    } finally {
      setReportFileLoading(false);
    }
  };

  const handleGenerateReportFile = async () => {
    if (!reportFileRestaurantId) return;
    if (reportFileDatesRequired && (!reportFileStartDate || !reportFileEndDate)) return;
    if (reportFileGroupBlocked || reportFileCountsBlocked) return;
    if (reportFileEmail && !isReportFileEmailValid) {
      setReportFileMessage({ type: 'error', text: t('adminTesting.generateReport.invalidEmail') });
      return;
    }
    const groupId = reportFileGroupIdToSend;
    try {
      setReportFileLoading(true);
      setReportFileMessage(null);
      setDataWarnings([]);
      const result: GenerateReportFileResult = await generateReportFile(
        reportFileRestaurantId,
        reportFileType,
        reportFileStartDateToSend,
        reportFileEndDateToSend,
        reportFileLanguage,
        groupId,
        reportFileBaselineToSend,
        reportFileLeakageUnitToSend,
        reportFileCountOptions
      );
      // Decode each returned PDF and trigger one download per file. A short stagger between
      // clicks avoids some browsers silently dropping near-simultaneous downloads triggered
      // from a single click handler (e.g. the inventory report's count-review + leakage PDFs).
      result.files.forEach((file, index) => {
        setTimeout(() => {
          const byteChars = atob(file.pdfBase64);
          const byteNumbers = new Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, index * 300);
      });

      const fileCount = result.files.length;
      const firstFilename = result.files[0]?.filename ?? '';
      const filenames = result.files.map(f => f.filename).join(', ');

      // Optionally also email a copy when a valid recipient was entered. Kept in its own
      // try/catch: the file(s) have already downloaded successfully at this point, so an email
      // failure here is a distinct, less severe outcome than a failed generation.
      const shouldEmail = reportFileEmail && isReportFileEmailValid;
      if (shouldEmail) {
        await runTestEmail({
          groupId,
          fileCount,
          firstFilename,
          restaurantName: result.restaurantName,
        });
      } else {
        setReportFileMessage({
          type: 'success',
          text: fileCount > 1
            ? t('adminTesting.generateReport.successMultiple', { restaurantName: result.restaurantName, count: fileCount, filenames })
            : t('adminTesting.generateReport.success', { restaurantName: result.restaurantName, filename: firstFilename }),
        });
      }
    } catch (err: any) {
      setReportFileMessage({ type: 'error', text: err.message || t('adminTesting.generateReport.error') });
    } finally {
      setReportFileLoading(false);
    }
  };

  /**
   * The monthly report is the weekly one over a whole calendar month, so picking it snaps the
   * window to the previous month — the period a scheduled run would cover. Both dates stay
   * editable; switching away leaves whatever window is there rather than snapping back.
   */
  const applyMonthlyWindow = () => {
    const { start, end } = previousPeriodWindow('month');
    setReportFileStartDate(start);
    setReportFileEndDate(end);
  };

  const handleChangeReportFileType = (nextType: AdminReportType) => {
    setReportFileType(nextType);
    setReportFileGroupId('');
    setReportFileBaselineSessionId('');
    setReportFileCountSessionId('');
    if (nextType === 'monthly') applyMonthlyWindow();
  };

  /** The window the report will actually cover, for the real-send confirm dialog. */
  const reportFilePeriodLabel = (): string => {
    if (reportFileDatesRequired) return `${reportFileStartDate} – ${reportFileEndDate}`;
    const groupName = reportFileSelectedGroup?.name ?? reportFileGroupId;
    if (reportFileCountSupported && reportFileEndingCount) {
      return `${groupName}: ${reportFileStartDateToSend} – ${reportFileEndDateToSend}`;
    }
    if (reportFileGroupRequired) return t('adminTesting.generateReport.groupPeriodShort', { groupName });
    return t('adminTesting.generateReport.inventoryPeriodShort');
  };

  /** The real send. Open data alerts never stop it — they come back as `dataWarnings`. */
  const runRealSend = async () => {
    try {
      setReportFileLoading(true);
      setReportFileMessage(null);
      setDataWarnings([]);
      const result = await sendReportEmail(
        reportFileRestaurantId,
        reportFileType,
        reportFileStartDateToSend,
        reportFileEndDateToSend,
        reportFileSelectedEmails,
        reportFileLanguage,
        false,
        reportFileGroupIdToSend,
        // Carried on the real send too: the control is visible on this card, so ignoring it
        // here would mail a unit the picker does not show. Still writes nothing.
        reportFileBaselineToSend,
        reportFileLeakageUnitToSend,
        reportFileCountOptions
      );
      setDataWarnings(result.dataWarnings ?? []);
      const skippedMessage = result.skipped?.length
        ? t('adminTesting.reportRecipientsSkipped', { emails: result.skipped.map(recipient => recipient.email).join(', ') })
        : '';
      if (result.skipped?.length && result.sent.length === 0 && result.failed.length === 0) {
        setReportFileMessage({ type: 'success', text: t('adminTesting.reportAllRecipientsSkipped') });
        return;
      }
      setReportFileMessage({
        type: result.failed.length > 0 ? 'error' : 'success',
        text: [result.failed.length > 0
          ? t('adminTesting.generateReport.sendRealPartial', {
              restaurantName: result.restaurantName,
              sent: result.sent.join(', '),
              failed: result.failed.join(', '),
            })
          : t('adminTesting.generateReport.sendRealSuccess', {
              restaurantName: result.restaurantName,
              count: result.sent.length,
              recipients: result.sent.join(', '),
            }), skippedMessage].filter(Boolean).join(' '),
      });
    } catch (err: any) {
      setReportFileMessage({ type: 'error', text: err.message || t('adminTesting.generateReport.error') });
    } finally {
      setReportFileLoading(false);
    }
  };

  /**
   * Real send: mails the report to the ticked admin/staff with no "[TEST] " prefix.
   * Unlike the test flow this does not download the PDFs — it only sends.
   */
  const handleSendRealReport = async () => {
    if (!reportFileRestaurantId || reportFileSelectedEmails.length === 0) return;
    if (reportFileDatesRequired && (!reportFileStartDate || !reportFileEndDate)) return;
    if (reportFileGroupBlocked || reportFileCountsBlocked) return;

    const restaurantName = restaurants.find(r => r.id === reportFileRestaurantId)?.name ?? reportFileRestaurantId;
    const period = reportFilePeriodLabel();
    const confirmed = window.confirm(
      t('adminTesting.generateReport.sendRealConfirm', {
        restaurantName,
        reportType: reportFileType,
        period,
        language: reportFileLanguage.toUpperCase(),
        recipients: reportFileSelectedEmails.join('\n'),
      })
    );
    if (!confirmed) return;

    await runRealSend();
  };

  const groupSelectedOwner = groupOwners?.find(o => o.ownerUserId === groupOwnerId) ?? null;

  // List the account's people once. Venue checkboxes control the report contents, never
  // recipient eligibility; a staff member assigned to several venues appears only once.
  useEffect(() => {
    let cancelled = false;
    setGroupRecipientOptions(null);
    setGroupSelectedEmails([]);
    setGroupRecipientsInput('');
    setGroupRecipientsError(false);
    setGroupRecipientsLoading(!!groupSelectedOwner);
    if (!groupSelectedOwner) return;
    const load = async () => {
      try {
        const lists = await Promise.all(groupSelectedOwner.restaurants.map(async venue => ({
          venue, recipients: await getReportRecipients(venue.id),
        })));
        if (cancelled) return;
        const staff = new Map<string, RecipientStaffWithVenues>();
        for (const { venue, recipients } of lists) {
          for (const person of recipients.staff) {
            const email = person.email.trim().toLowerCase();
            const existing = staff.get(email);
            if (existing) existing.venueNames!.push(venue.name);
            else staff.set(email, { ...person, email, venueNames: [venue.name] });
          }
        }
        const owner = lists.find(entry => entry.recipients.owner)?.recipients.owner ?? null;
        const ownerEmail = owner?.email.trim().toLowerCase();
        if (ownerEmail) staff.delete(ownerEmail);
        setGroupRecipientOptions({
          owner: owner && ownerEmail ? { ...owner, email: ownerEmail } : null,
          staff: Array.from(staff.values()).sort((a, b) => a.email.localeCompare(b.email)),
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load group report recipients:', err);
        setGroupRecipientsError(true);
      } finally {
        if (!cancelled) setGroupRecipientsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [groupSelectedOwner]);

  // Picking another owner (or clearing the pick) re-ticks every venue of the new account —
  // ids from the previous owner would otherwise silently shrink the next send.
  useEffect(() => {
    setGroupVenueIds(groupSelectedOwner ? groupSelectedOwner.restaurants.map(r => r.id) : []);
  }, [groupSelectedOwner]);

  const toggleGroupVenue = (restaurantId: string) => {
    setGroupMessage(null);
    setGroupSentFilenames([]);
    setGroupVenueIds(prev =>
      prev.includes(restaurantId) ? prev.filter(id => id !== restaurantId) : [...prev, restaurantId]
    );
  };

  const setAllGroupVenues = (selected: boolean) => {
    setGroupMessage(null);
    setGroupSentFilenames([]);
    setGroupVenueIds(selected && groupSelectedOwner ? groupSelectedOwner.restaurants.map(r => r.id) : []);
  };

  /** The ticked venues in the owner's own order — what the confirm dialog reads out. */
  const groupSelectedVenueNames =
    groupSelectedOwner?.restaurants.filter(r => groupVenueIds.includes(r.id)).map(r => r.name) ?? [];

  const groupRecipients = parseRecipientList([...groupSelectedEmails, groupRecipientsInput].join(' '));
  const groupInvalidRecipients = groupRecipients.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  const groupBlocked =
    !groupOwnerId || groupRecipientsLoading ||
    // A group overview of one venue is just the individual report — the server 400s on it too.
    groupVenueIds.length < 2 ||
    groupRecipients.length === 0 ||
    groupInvalidRecipients.length > 0 ||
    groupRecipients.length > MAX_REPORT_RECIPIENTS ||
    !reportFileStartDate ||
    !reportFileEndDate;

  /**
   * The group send. The ticked venues are sent along, but the server still applies the per-type
   * master switch on top — a venue switched off on Communication → Settings will be missing from
   * the returned `restaurantsIncluded` even though it was ticked here.
   */
  const handleSendGroupReport = async () => {
    if (groupBlocked || groupLoading || !groupSelectedOwner) return;

    if (!groupIsTest) {
      const confirmed = window.confirm(
        t('adminTesting.generateReport.groupSendRealConfirm', {
          ownerName: groupSelectedOwner.ownerName || groupSelectedOwner.ownerUserId,
          reportType: groupReportType,
          period: `${reportFileStartDate} – ${reportFileEndDate}`,
          language: reportFileLanguage.toUpperCase(),
          venues: groupSelectedVenueNames.join(', '),
          recipients: groupRecipients.join('\n'),
        })
      );
      if (!confirmed) return;
    }

    try {
      setGroupLoading(true);
      setGroupMessage(null);
      setGroupSentFilenames([]);
      const result = await sendGroupReport({
        ownerUserId: groupOwnerId,
        reportType: groupReportType,
        startDate: reportFileStartDate,
        endDate: reportFileEndDate,
        language: reportFileLanguage,
        recipients: groupRecipients,
        isTest: groupIsTest,
        restaurantIds: groupVenueIds,
      });
      setGroupSentFilenames(result.sent.length > 0 ? result.filenames : []);
      const skippedMessage = result.skipped?.length
        ? t('adminTesting.reportRecipientsSkipped', { emails: result.skipped.map(recipient => recipient.email).join(', ') })
        : '';
      if (result.skipped?.length && result.sent.length === 0 && result.failed.length === 0) {
        setGroupMessage({ type: 'success', text: t('adminTesting.reportAllRecipientsSkipped') });
        return;
      }
      setGroupMessage({
        type: result.failed.length > 0 ? 'error' : 'success',
        text: [result.failed.length > 0
          ? t('adminTesting.generateReport.groupSendPartial', {
              sent: result.sent.join(', '),
              failed: result.failed.map(f => `${f.email} (${f.error})`).join(', '),
            })
          : t('adminTesting.generateReport.groupSendSuccess', {
              count: result.sent.length,
              recipients: result.sent.join(', '),
              venues: result.restaurantsIncluded.map(r => r.name).join(', '),
            }), skippedMessage].filter(Boolean).join(' '),
      });
    } catch (err: any) {
      setGroupMessage({ type: 'error', text: err.message || t('adminTesting.generateReport.groupSendError') });
    } finally {
      setGroupLoading(false);
    }
  };

  /** Both modes mail live customers when the test flag is off — the card says so in red. */
  const isRealSend = mode === 'single' ? reportFileSendReal : !groupIsTest;

  return (
    /* Generate Report Files Card — turns red in real-send mode (mails live customers) */
    <div className={cn(
      cardClass,
      'p-6',
      isRealSend && 'border-2 border-red-500 dark:border-red-500 bg-red-50/50 dark:bg-red-900/10'
    )}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.generateReport.title')}</h3>
          <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
            {t('adminTesting.generateReport.description')}
          </p>
        </div>
        {/* The real-send switch belongs to single mode; group mode has its own test checkbox. */}
        {mode === 'single' && (
          <label className="flex items-center gap-2 shrink-0 cursor-pointer">
            <input
              type="checkbox"
              checked={reportFileSendReal}
              onChange={(e) => { setReportFileSendReal(e.target.checked); setReportFileMessage(null); }}
              className="w-4 h-4 accent-red-600"
            />
            <span className={cn(smallTextSize, semiBoldText, reportFileSendReal ? 'text-red-600 dark:text-red-400' : secondaryText)}>
              {t('adminTesting.generateReport.sendRealToggle')}
            </span>
          </label>
        )}
      </div>

      {/* Single restaurant vs. one combined email for a whole admin account */}
      <div className="flex flex-wrap gap-1 p-1 mb-4 rounded-lg bg-gray-100 dark:bg-gray-800 w-fit">
        {(['single', 'group'] as const).map(value => (
          <button
            key={value}
            type="button"
            onClick={() => { setMode(value); setReportFileMessage(null); setGroupMessage(null); setDataWarnings([]); }}
            className={cn(
              'px-3 py-1.5 rounded-md transition-colors',
              smallTextSize, semiBoldText,
              value === mode
                ? cn('bg-white dark:bg-gray-700 shadow-sm', primaryText)
                : cn(secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-700/50')
            )}
          >
            {value === 'single'
              ? t('adminTesting.generateReport.modeSingle')
              : t('adminTesting.generateReport.modeGroup')}
          </button>
        ))}
      </div>

      {isRealSend && (
        <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
          {t('adminTesting.generateReport.sendRealWarning')}
        </div>
      )}

      {/* Restaurant selector */}
      {mode === 'single' && (
      <div className="mb-4">
        <input
          type="text"
          placeholder={t('adminTesting.generateReport.searchPlaceholder')}
          value={reportFileSearchQuery}
          onChange={(e) => setReportFileSearchQuery(e.target.value)}
          className={cn(
            'w-full px-3 py-2 rounded-lg border mb-2',
            'border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-700',
            primaryText, smallTextSize
          )}
        />
        <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-3">
          {reportFileFilteredRestaurants.map(r => (
            <button
              key={r.id}
              onClick={() => { setReportFileRestaurantId(r.id); setReportFileGroupId(''); setReportFileBaselineSessionId(''); setReportFileCountSessionId(''); }}
              className={cn(
                'w-full text-left px-3 py-2 transition-colors',
                smallTextSize,
                reportFileRestaurantId === r.id
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              )}
            >
              <div className={cn(primaryText, semiBoldText)}>{r.name}</div>
              <div className={cn(secondaryText, 'text-xs')}>{r.ownerEmail}</div>
            </button>
          ))}
        </div>

        {/* Report type */}
        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.reportTypeLabel')}</label>
          <select
            value={reportFileType}
            onChange={(e) => handleChangeReportFileType(e.target.value as AdminReportType)}
            className={cn(
              'w-full px-3 py-2 rounded-lg border',
              'border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700',
              primaryText, smallTextSize
            )}
          >
            <option value="revenue">{t('adminTesting.generateReport.reportTypeRevenue')}</option>
            <option value="weekly">{t('adminTesting.generateReport.reportTypeWeekly')}</option>
            <option value="monthly">{t('adminTesting.generateReport.reportTypeMonthly')}</option>
            <option value="waste">{t('adminTesting.generateReport.reportTypeWaste')}</option>
            <option value="inventoryLeakage">{t('adminTesting.generateReport.reportTypeInventoryLeakage')}</option>
            <option value="inventoryOrder">{t('adminTesting.generateReport.reportTypeInventoryOrder')}</option>
            <option value="inventoryManagement">{t('adminTesting.generateReport.reportTypeInventoryManagement')}</option>
            <option value="priceAlerts">{t('adminTesting.generateReport.reportTypePriceAlerts')}</option>
          </select>
        </div>

        {/* Inventory group — required for the per-group types, optional (= combined) for waste */}
        {reportFileGroupUsed && (
          <div className="mb-3">
            <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.groupLabel')}</label>
            {!reportFileRestaurantId ? (
              <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.groupPickRestaurant')}</p>
            ) : reportFileGroupsLoading ? (
              <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.groupsLoading')}</p>
            ) : !reportFileGroups || reportFileGroups.length === 0 ? (
              <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.groupsNone')}</p>
            ) : (
              <select
                value={reportFileGroupId}
                onChange={(e) => { setReportFileGroupId(e.target.value); setReportFileBaselineSessionId(''); setReportFileCountSessionId(''); }}
                className={cn(
                  'w-full px-3 py-2 rounded-lg border',
                  'border-gray-300 dark:border-gray-600',
                  'bg-white dark:bg-gray-700',
                  primaryText, smallTextSize
                )}
              >
                <option value="">
                  {reportFileGroupOptional
                    ? t('adminTesting.generateReport.groupCombinedOption')
                    : t('adminTesting.generateReport.groupPlaceholder')}
                </option>
                {reportFileGroups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            )}
            {reportFileGroupMissingMinimums && (
              <div className={cn(
                'mt-2 p-2 rounded-lg border',
                'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10',
                'text-amber-700 dark:text-amber-300 text-xs'
              )}>
                {t('adminTesting.generateReport.groupNoMinimumsWarning')}
              </div>
            )}
          </div>
        )}

        {reportFileCountSupported && (
          <div className="space-y-3 mb-3">
            <div>
              <label className={cn(secondaryText, 'text-xs block mb-1')} htmlFor="report-ending-count">
                {t('analysisReports.picker.endCount')}
              </label>
              <select
                id="report-ending-count"
                value={reportFileEndingCount?.sessionId ?? ''}
                onChange={e => { setReportFileCountSessionId(e.target.value); setReportFileBaselineSessionId(''); }}
                disabled={reportFileGroupsLoading || !reportFileEndingCount}
                className={cn('w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize)}
              >
                {!reportFileEndingCount && <option value="">{t('analysisReports.picker.noCounts')}</option>}
                {reportFileSelectedGroup?.counts.map(c => (
                  <option key={c.sessionId} value={c.sessionId}>
                    {new Date(c.completedAt).toLocaleString('en-GB')} · {currencyFormatter.format(c.totalValue)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={cn(secondaryText, 'text-xs block mb-1')} htmlFor="report-starting-count">
                {t('analysisReports.picker.startCount')}
              </label>
              <select
                id="report-starting-count"
                value={reportFileBaselineToSend ?? ''}
                onChange={e => setReportFileBaselineSessionId(e.target.value)}
                disabled={reportFileGroupsLoading || reportFileBaselineOptions.length === 0}
                className={cn('w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize)}
              >
                {reportFileBaselineOptions.length === 0 && <option value="">{t('analysisReports.picker.noOlderCounts')}</option>}
                {reportFileBaselineOptions.map(c => (
                  <option key={c.sessionId} value={c.sessionId}>
                    {new Date(c.completedAt).toLocaleString('en-GB')} · {currencyFormatter.format(c.totalValue)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/*
          Leakage only: which unit its QUANTITIES print in, for THIS run. Deliberately not the
          restaurant's setting (Settings tab) — it is here so both outputs can be checked for a
          live client without writing anything. Euro figures are identical either way.
        */}
        {reportFileType === 'inventoryLeakage' && (
          <div className="mb-3">
            <label className={cn(secondaryText, 'text-xs block mb-1')}>
              {t('adminTesting.generateReport.leakageUnitLabel')}
            </label>
            <select
              value={reportFileLeakageUnit}
              onChange={(e) => setReportFileLeakageUnit(e.target.value as '' | LeakageUnitPreview)}
              className={cn(
                'w-full px-3 py-2 rounded-lg border',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            >
              <option value="">{t('adminTesting.generateReport.leakageUnitStored')}</option>
              <option value="std">{t('adminTesting.generateReport.leakageUnitStd')}</option>
              <option value="count">{t('adminTesting.generateReport.leakageUnitCount')}</option>
            </select>
            <p className={cn(secondaryText, 'text-xs mt-1')}>
              {t('adminTesting.generateReport.leakageUnitHint')}
            </p>
          </div>
        )}

        {/* Report language (PDF + email) */}
        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.languageLabel')}</label>
          <select
            value={reportFileLanguage}
            onChange={(e) => setReportFileLanguage(e.target.value as AdminReportLanguage)}
            className={cn(
              'w-full px-3 py-2 rounded-lg border',
              'border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700',
              primaryText, smallTextSize
            )}
          >
            <option value="en">{t('adminTesting.generateReport.languageEnglish')}</option>
            <option value="nl">{t('adminTesting.generateReport.languageDutch')}</option>
          </select>
        </div>

        {reportFileDatesRequired && (
          <>
            {/* Date range */}
            <div className="flex gap-3 mb-1">
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.startDateLabel')}</label>
                <input
                  type="date"
                  value={reportFileStartDate}
                  onChange={(e) => setReportFileStartDate(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                />
              </div>
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.endDateLabel')}</label>
                <input
                  type="date"
                  value={reportFileEndDate}
                  onChange={(e) => setReportFileEndDate(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                />
              </div>
            </div>
            <div className="mb-3">
              {reportFileType === 'monthly' && (
                <p className={cn(secondaryText, 'text-xs')}>
                  {t('adminTesting.generateReport.monthlyPeriodHint')}
                </p>
              )}

            </div>
          </>
        )}
        {!reportFileDatesRequired && !reportFileCountSupported && (
          <p className={cn(secondaryText, 'text-xs mb-3')}>
            {t(reportFileGroupRequired ? 'adminTesting.generateReport.groupPeriodHint' : 'adminTesting.generateReport.inventoryPeriodHint')}
          </p>
        )}

        {reportFileSendReal ? (
          /* Real send: pick who at the client receives it (admin pre-ticked, staff opt-in) */
          <div className="mb-3">
            <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.recipientsLabel')}</label>
            {!reportFileRestaurantId ? (
              <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.recipientsPickRestaurant')}</p>
            ) : reportFileRecipientsLoading ? (
              <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.recipientsLoading')}</p>
            ) : !reportFileRecipients || (!reportFileRecipients.owner && reportFileRecipients.staff.length === 0) ? (
              <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.recipientsNone')}</p>
            ) : (
              <ReportRecipientPicker
                key={reportFileRestaurantId}
                recipients={reportFileRecipients}
                selectedEmails={reportFileSelectedEmails}
                onChange={setReportFileSelectedEmails}
                t={t}
              />
            )}
          </div>
        ) : (
          /* Test send: email a copy to one address (optional) */
          <div className="mb-3">
            <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.emailLabel')}</label>
            <input
              type="email"
              placeholder={t('adminTesting.generateReport.emailPlaceholder')}
              value={reportFileEmail}
              onChange={(e) => setReportFileEmail(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-lg border',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            />
          </div>
        )}

        {reportFileSendReal && reportFileSelectedEmails.length > MAX_REPORT_RECIPIENTS && (
          <div
            className={cn(
              'mb-3 p-3 rounded-lg border',
              'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20',
              'text-red-700 dark:text-red-300',
              smallTextSize,
              semiBoldText
            )}
          >
            {t('adminTesting.generateReport.recipientsTooMany', {
              count: reportFileSelectedEmails.length,
              max: MAX_REPORT_RECIPIENTS,
              over: reportFileSelectedEmails.length - MAX_REPORT_RECIPIENTS,
            })}
          </div>
        )}

        {reportFileSendReal ? (
          <button
            onClick={handleSendRealReport}
            disabled={
              !reportFileRestaurantId ||
              reportFileSelectedEmails.length === 0 ||
              (reportFileDatesRequired && (!reportFileStartDate || !reportFileEndDate)) ||
              reportFileGroupBlocked || reportFileCountsBlocked ||
              reportFileLoading
            }
            className={cn(
              'px-4 py-2 rounded-lg transition-colors',
              'bg-red-600 dark:bg-red-600 hover:bg-red-700 dark:hover:bg-red-700 text-white dark:text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              smallTextSize, semiBoldText
            )}
          >
            {reportFileLoading
              ? t('adminTesting.generateReport.sending')
              : t('adminTesting.generateReport.sendRealButton', { count: reportFileSelectedEmails.length })}
          </button>
        ) : (
          <button
            onClick={handleGenerateReportFile}
            disabled={
              !reportFileRestaurantId ||
              (reportFileDatesRequired && (!reportFileStartDate || !reportFileEndDate)) ||
              reportFileGroupBlocked || reportFileCountsBlocked ||
              (!!reportFileEmail && !isReportFileEmailValid) ||
              reportFileLoading
            }
            className={cn(
              'px-4 py-2 rounded-lg transition-colors',
              'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              smallTextSize, semiBoldText
            )}
          >
            {reportFileLoading ? t('adminTesting.generateReport.generating') : t('adminTesting.generateReport.generateButton')}
          </button>
        )}
      </div>
      )}

      {/* Group send: one combined email per recipient for a whole admin account */}
      {mode === 'group' && (
      <div className="mb-4">
        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.groupOwnerLabel')}</label>
          {groupOwnersLoading ? (
            <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.groupOwnersLoading')}</p>
          ) : groupOwnersError ? (
            <p className="text-xs text-red-600 dark:text-red-400">
              {t('adminTesting.generateReport.groupOwnersError')}{' '}
              <button
                type="button"
                onClick={() => setGroupOwnersError(false)}
                className="underline"
              >
                {t('adminTesting.reportSettings.retry')}
              </button>
            </p>
          ) : !groupOwners || groupOwners.length === 0 ? (
            <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.groupOwnersNone')}</p>
          ) : (
            <select
              value={groupOwnerId}
              onChange={(e) => { setGroupOwnerId(e.target.value); setGroupMessage(null); setGroupSentFilenames([]); }}
              className={cn(
                'w-full px-3 py-2 rounded-lg border',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            >
              <option value="">{t('adminTesting.generateReport.groupOwnerPlaceholder')}</option>
              {groupOwners.map(owner => (
                <option key={owner.ownerUserId} value={owner.ownerUserId}>
                  {t('adminTesting.generateReport.groupOwnerOption', {
                    ownerName: owner.ownerName || owner.ownerUserId,
                    count: owner.restaurants.length,
                  })}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Which venues the email carries — ticked by default, untick to simulate a smaller group */}
        {groupSelectedOwner && (
          <div className="mb-3">
            {/* Wraps rather than squashing: on a narrow screen the two buttons drop below the label. */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <label className={cn(secondaryText, 'text-xs')}>
                {t('adminTesting.generateReport.groupVenuesLabel')}{' '}
                <span className={semiBoldText}>
                  {t('adminTesting.generateReport.groupVenuesCount', {
                    selected: groupVenueIds.length,
                    total: groupSelectedOwner.restaurants.length,
                  })}
                </span>
              </label>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setAllGroupVenues(true)}
                  disabled={groupVenueIds.length === groupSelectedOwner.restaurants.length}
                  className={cn(
                    'px-2 py-0.5 rounded-full border text-xs transition-colors',
                    'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                    'hover:bg-gray-50 dark:hover:bg-gray-600',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    secondaryText
                  )}
                >
                  {t('adminTesting.generateReport.groupVenuesSelectAll')}
                </button>
                <button
                  type="button"
                  onClick={() => setAllGroupVenues(false)}
                  disabled={groupVenueIds.length === 0}
                  className={cn(
                    'px-2 py-0.5 rounded-full border text-xs transition-colors',
                    'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                    'hover:bg-gray-50 dark:hover:bg-gray-600',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    secondaryText
                  )}
                >
                  {t('adminTesting.generateReport.groupVenuesDeselectAll')}
                </button>
              </div>
            </div>
            <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
              {groupSelectedOwner.restaurants.map(r => (
                <label key={r.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={groupVenueIds.includes(r.id)}
                    onChange={() => toggleGroupVenue(r.id)}
                    className="w-4 h-4 accent-purple-600 shrink-0"
                  />
                  <span className={cn(primaryText, smallTextSize, 'min-w-0 truncate')}>{r.name}</span>
                </label>
              ))}
            </div>
            {groupVenueIds.length < 2 && (
              <p className="text-xs mt-1 text-red-600 dark:text-red-400">
                {t('adminTesting.generateReport.groupVenuesTooFew', { count: groupVenueIds.length })}
              </p>
            )}
            <p className={cn(secondaryText, 'text-xs mt-1')}>{t('adminTesting.generateReport.groupVenuesHint')}</p>
          </div>
        )}

        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.reportTypeLabel')}</label>
          <select
            value={groupReportType}
            onChange={(e) => {
              const nextType = e.target.value as GroupReportType;
              setGroupReportType(nextType);
              if (nextType === 'monthly') applyMonthlyWindow();
            }}
            className={cn(
              'w-full px-3 py-2 rounded-lg border',
              'border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700',
              primaryText, smallTextSize
            )}
          >
            {GROUP_REPORT_TYPES.map(type => (
              <option key={type} value={type}>{t(GROUP_REPORT_TYPE_LABEL_KEYS[type])}</option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.languageLabel')}</label>
          <select
            value={reportFileLanguage}
            onChange={(e) => setReportFileLanguage(e.target.value as AdminReportLanguage)}
            className={cn(
              'w-full px-3 py-2 rounded-lg border',
              'border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700',
              primaryText, smallTextSize
            )}
          >
            <option value="en">{t('adminTesting.generateReport.languageEnglish')}</option>
            <option value="nl">{t('adminTesting.generateReport.languageDutch')}</option>
          </select>
        </div>

        {/* Same window state as single mode, so switching modes keeps the period you picked */}
        <div className="flex gap-3 mb-3">
          <div className="flex-1">
            <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.startDateLabel')}</label>
            <input
              type="date"
              value={reportFileStartDate}
              onChange={(e) => setReportFileStartDate(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-lg border',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            />
          </div>
          <div className="flex-1">
            <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.endDateLabel')}</label>
            <input
              type="date"
              value={reportFileEndDate}
              onChange={(e) => setReportFileEndDate(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-lg border',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            />
          </div>
        </div>

        {groupSelectedOwner && (
          <div className="mb-3">
            <p className={cn(secondaryText, 'text-xs mb-2')}>{t('adminTesting.generateReport.groupRecipientSelectionHint')}</p>
            {groupRecipientsLoading ? (
              <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.generateReport.recipientsLoading')}</p>
            ) : groupRecipientsError ? (
              <p className="text-xs text-red-600 dark:text-red-400">{t('adminTesting.generateReport.recipientsError')}</p>
            ) : groupRecipientOptions ? (
              <ReportRecipientPicker
                key={groupOwnerId}
                recipients={groupRecipientOptions}
                selectedEmails={groupSelectedEmails}
                onChange={setGroupSelectedEmails}
                t={t}
              />
            ) : null}
          </div>
        )}

        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.generateReport.additionalRecipientsLabel')}</label>
          <textarea
            rows={2}
            placeholder={t('adminTesting.generateReport.groupRecipientsPlaceholder')}
            value={groupRecipientsInput}
            onChange={(e) => setGroupRecipientsInput(e.target.value)}
            className={cn(
              'w-full px-3 py-2 rounded-lg border',
              'border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700',
              primaryText, smallTextSize
            )}
          />
          <p className={cn(secondaryText, 'text-xs mt-1')}>
            {t('adminTesting.generateReport.groupRecipientsHint', { count: groupRecipients.length, max: MAX_REPORT_RECIPIENTS })}
          </p>
          {groupInvalidRecipients.length > 0 && (
            <p className="text-xs mt-1 text-red-600 dark:text-red-400">
              {t('adminTesting.generateReport.groupRecipientsInvalid', { emails: groupInvalidRecipients.join(', ') })}
            </p>
          )}
          {groupRecipients.length > MAX_REPORT_RECIPIENTS && (
            <p className="text-xs mt-1 text-red-600 dark:text-red-400">
              {t('adminTesting.generateReport.recipientsTooMany', {
                count: groupRecipients.length,
                max: MAX_REPORT_RECIPIENTS,
                over: groupRecipients.length - MAX_REPORT_RECIPIENTS,
              })}
            </p>
          )}
        </div>

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={groupIsTest}
            onChange={(e) => { setGroupIsTest(e.target.checked); setGroupMessage(null); }}
            className="w-4 h-4 accent-red-600"
          />
          <span className={cn(smallTextSize, semiBoldText, groupIsTest ? secondaryText : 'text-red-600 dark:text-red-400')}>
            {t('adminTesting.generateReport.groupTestToggle')}
          </span>
        </label>

        <button
          onClick={handleSendGroupReport}
          disabled={groupBlocked || groupLoading}
          className={cn(
            'px-4 py-2 rounded-lg transition-colors text-white dark:text-white',
            groupIsTest
              ? 'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700'
              : 'bg-red-600 dark:bg-red-600 hover:bg-red-700 dark:hover:bg-red-700',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            smallTextSize, semiBoldText
          )}
        >
          {groupLoading
            ? t('adminTesting.generateReport.sending')
            : t('adminTesting.generateReport.groupSendButton', { count: groupRecipients.length })}
        </button>
      </div>
      )}

      {/* Group result — its own message so a group send never overwrites the single-send one */}
      {mode === 'group' && groupMessage && (
        <div className={cn(
          'p-3 rounded-lg text-sm mb-4',
          groupMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        )}>
          <p>{groupMessage.text}</p>
          {groupSentFilenames.length > 0 && (
            <>
              <p className={cn('mt-2 text-xs', semiBoldText)}>
                {t('adminTesting.generateReport.groupFilesTitle', { count: groupSentFilenames.length })}
              </p>
              <ul className="mt-1 space-y-0.5">
                {groupSentFilenames.map(filename => (
                  <li key={filename} className="text-xs break-words">{filename}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Result message */}
      {mode === 'single' && reportFileMessage && (
        <div className={cn(
          'p-3 rounded-lg text-sm mb-4',
          reportFileMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        )}>
          {reportFileMessage.text}
        </div>
      )}

      {/* What the email said about incomplete data — never a reason the send did not happen */}
      {dataWarnings.length > 0 && <DataWarningsBanner warnings={dataWarnings} t={t} />}
    </div>
  );
}
